import { create } from "zustand";

import type { FollowSignal } from "../api/types";

/**
 * Сигналы копирования: карточки, которые ждут решения, и недавняя история.
 *
 * # Почему замещение по id, а не добавление
 *
 * Сигнал приезжает одним типом сообщения на ВСЕ свои состояния: появился, взят,
 * пропущен, сгорел. Отдельные типы «сигнал» и «решение» заставили бы склеивать
 * их по идентификатору руками, то есть держать порядок доставки, которого у
 * Pub/Sub нет. То же правило, что у order_update.
 *
 * # Потолок
 *
 * Сигналов единицы в минуту, поэтому потолок щедрый: он защищает не от потока, а
 * от вкладки, открытой сутками.
 */
const SIGNALS_LIMIT = 300;

interface SignalsState {
  /** Ключ — id сигнала. Порядок показа задаёт сортировка, а не вставка. */
  signals: Record<string, FollowSignal>;
  /** Одно сообщение сокета либо строка REST-ленты. */
  ingest: (signal: FollowSignal) => void;
  /** Пачка из REST: поднятие висящих карточек при подключении вкладки. */
  ingestMany: (signals: FollowSignal[]) => void;
  /**
   * Забыть сигналы, которых сервер в ленте ждущих решения больше не называет.
   *
   * Именно ЗАБЫТЬ, а не переписать исход: какой он на самом деле — «сгорел»,
   * «ордер выставлен» или «погашен правилом», — мы отсюда не знаем, и
   * подставленный наугад исход соврал бы в истории. Строка вернётся с истинным
   * исходом следующей выборкой ленты.
   */
  drop: (ids: readonly string[]) => void;
  clear: () => void;
}

/**
 * prune выбрасывает самые давние по времени обнаружения.
 *
 * По detected_at, а не по порядку прихода: REST отдаёт историю сразу пачкой, и
 * порядок вставки в ней ничего не значит.
 */
function prune(next: Record<string, FollowSignal>): Record<string, FollowSignal> {
  const ids = Object.keys(next);
  if (ids.length <= SIGNALS_LIMIT) return next;
  const sorted = ids.sort(
    (a, b) => Date.parse(next[b]?.detected_at ?? "") - Date.parse(next[a]?.detected_at ?? ""),
  );
  for (const id of sorted.slice(SIGNALS_LIMIT)) delete next[id];
  return next;
}

export const useSignals = create<SignalsState>((setState) => ({
  signals: {},

  ingest: (signal) => {
    if (!signal?.id) return;
    setState((state) => ({ signals: prune({ ...state.signals, [signal.id]: signal }) }));
  },

  ingestMany: (incoming) => {
    if (incoming.length === 0) return;
    setState((state) => {
      const next = { ...state.signals };
      for (const s of incoming) {
        if (s?.id) next[s.id] = s;
      }
      return { signals: prune(next) };
    });
  },

  drop: (ids) => {
    if (ids.length === 0) return;
    setState((state) => {
      const next = { ...state.signals };
      for (const id of ids) delete next[id];
      return { signals: next };
    });
  },

  clear: () => setState({ signals: {} }),
}));
